/**
 * Every grid page's address ends with the view it shows, as a data table's
 * does (`/tables/:id/table-views/:viewId/records`): `/automations/runs/views/
 * runs-by-status`. So a copied or shared address opens the same view.
 *
 * The page's own address (no view) stays a page of its own. It opens the
 * view the workspace was last on and then names it in the address, so
 * favorites, Home Pages, and links saved before views had addresses keep
 * working.
 */
export const GRID_VIEW_SEGMENT = 'views'

/** The id a grid with no saved views gives its one built-in view (All Runs, All Users). */
export const DEFAULT_GRID_VIEW_ID = 'all'

export interface GridViewAddress {
  /** The page's own address, without the view. */
  pagePath: string
  /** The view the address names, or null when it names none. */
  viewId: string | null
}

function decodeSegment(segment: string) {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/** Splits an address into its page and the view it names, if any. */
export function splitGridViewPath(pathname: string): GridViewAddress {
  const segments = pathname.split('/').filter(Boolean)
  const viewIndex = segments.length - 2
  if (viewIndex >= 0 && segments[viewIndex] === GRID_VIEW_SEGMENT) {
    return {
      pagePath: `/${segments.slice(0, viewIndex).join('/')}`,
      viewId: decodeSegment(segments[viewIndex + 1]),
    }
  }
  return { pagePath: `/${segments.join('/')}`, viewId: null }
}

/** The address of one of a grid page's views. */
export function gridViewPath(pagePath: string, viewId: string): string {
  const page = splitGridViewPath(pagePath).pagePath
  return `${page === '/' ? '' : page}/${GRID_VIEW_SEGMENT}/${encodeURIComponent(viewId)}`
}

/**
 * The views a grid's address can name, by id and name: its saved views, or
 * its one built-in view when it has none.
 */
export function gridViewOptions(
  views: readonly { id: string; name: string }[],
  defaultViewName: string
): { value: string; label: string }[] {
  if (views.length === 0) return [{ value: DEFAULT_GRID_VIEW_ID, label: defaultViewName }]
  return views.map((view) => ({ value: view.id, label: view.name }))
}
